import { ToastrService } from 'ngx-toastr';

import { Component, OnInit, Inject, ViewChild, ElementRef } from '@angular/core';

import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormGroup, FormControl, FormBuilder, Validators } from '@angular/forms';

import { GoogleMap } from '@angular/google-maps';

import { SpinnerService } from '../../services/spinner.service';
import { ApiShelter, ApiSetSheltersRequest } from '../../api-client-module';
import { ProjectLanguage } from '../../firestore-models/project-language';

import { ConfigurationService } from '../../configuration.service';
import { ApiClientService } from '../../api-client.service';
import { AppConst } from '../../app-const';
import { MultiNameDialogComponent } from '../../common/multi-name-dialog/multi-name-dialog.component';
import { TranslateService } from '@ngx-translate/core';

export interface ShelterDialogData {
  change_type: number;
  shelter?: ApiShelter;
}

@Component({
  selector: 'app-shelter-dialog',
  templateUrl: './shelter-dialog.component.html',
  styleUrls: ['./shelter-dialog.component.css'],
  standalone: false,
})
export class ShelterDialogComponent implements OnInit {

  projectId: string;
  //建物情報
  shelter?: ApiShelter;
  isAdded: boolean = false;
  projectLanguages!: Array<ProjectLanguage>;

  //建物情報フォーム
  shelterFormGroup!: FormGroup;
  shelterIdControl!: FormControl;
  shelterNameControl!: FormControl;
  shelterLatControl!: FormControl;
  shelterLngControl!: FormControl;
  showMarkerControl!: FormControl;

  //GoogleMap表示
  zoom = AppConst.ZOOM_INITIAL_VALUE;
  center!: google.maps.LatLngLiteral;
  options: google.maps.MapOptions = {
    disableDefaultUI: true
  };
  currentPosition!: google.maps.LatLngLiteral
  currentPositionMarkerOption: google.maps.MarkerOptions = {
    draggable: true
  };

  @ViewChild(GoogleMap, { static: false }) map!: GoogleMap;
  @ViewChild("mapSearchField") searchField!: ElementRef;
  @ViewChild("mapSearchFieldExecute") searchFieldExecute!: ElementRef;
  @ViewChild("mapSearchFieldClear") searchFieldClear!: ElementRef;
  searchBox!: google.maps.places.SearchBox;

  projectBaseLang: string = 'ja';

  constructor(
    public dialogRef: MatDialogRef<ShelterDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ShelterDialogData,
    private formBuilder: FormBuilder,
    public dialog: MatDialog,
    private configurationService: ConfigurationService,
    private translate: TranslateService,
    private apiClientService: ApiClientService,
    private spinnerService: SpinnerService,
    private toastr: ToastrService,
  ) {
    this.projectId = this.configurationService.getProjectId();
  }

  ngOnInit(): void {
    this.projectLanguages = this.configurationService.getProject().languages!;
    this.shelter = this.data.shelter;
    if (!this.shelter) {
      this.shelter = {
        shelter_id: '',
        shelter_names: [],
        latitude: this.configurationService.getProject().project_geopoint && this.configurationService.getProject().project_geopoint.latitude ? this.configurationService.getProject().project_geopoint.latitude : AppConst.LATITUDE_INITIAL_VALUE,
        longitude: this.configurationService.getProject().project_geopoint && this.configurationService.getProject().project_geopoint.longitude ? this.configurationService.getProject().project_geopoint.longitude : AppConst.LONGITUDE_INITIAL_VALUE,
        show_marker: false,
        project_id: this.projectId,
        enabled: true,
      };
      this.isAdded = true;
    }
    this.currentPosition = {
      lat: this.shelter.latitude!,
      lng: this.shelter.longitude!
    }
    this.center = this.currentPosition;
    this.projectBaseLang = this.configurationService.getProject().project_base_lang;

    this.createForm();
    this.shelterIdControl = this.shelterFormGroup.get('shelterId') as FormControl;
    this.shelterNameControl = this.shelterFormGroup.get('shelterName') as FormControl;
    this.shelterLatControl = this.shelterFormGroup.get('shelterLat') as FormControl;
    this.shelterLngControl = this.shelterFormGroup.get('shelterLng') as FormControl;
    this.showMarkerControl = this.shelterFormGroup.get('showMarker') as FormControl;
  }

  ngAfterViewInit(): void {
    // 住所検索用
    this.searchBox = new google.maps.places.SearchBox(
      this.searchField.nativeElement
    );
    this.map.controls[google.maps.ControlPosition.TOP_CENTER].push(
      this.searchField.nativeElement
    );
    this.map.controls[google.maps.ControlPosition.TOP_CENTER].push(
      this.searchFieldExecute.nativeElement
    );
    this.map.controls[google.maps.ControlPosition.TOP_CENTER].push(
      this.searchFieldClear.nativeElement
    );
  }

  //GoogleMap上のピンとフォームの値を一致させる
  onMerkerDragEnd(event: any) {
    this.shelterLatControl.setValue(this.round(event.latLng!.lat(), 8));
    this.shelterLngControl.setValue(this.round(event.latLng!.lng(), 8));
  }

  //ピンの位置
  private round(value: number, digits: number): number {
    let pow: number = 1;
    if (digits > 0) {
      pow = Math.pow(10, digits);
    }
    return Math.round(value * pow) / pow;
  }

  //Googleマップと緯度のフォーム連携
  onBlurMarkerLat() {
    if (!!this.shelterLatControl.valid) {
      this.shelterLatControl.setValue(this.round(this.shelterLatControl.value, 8));
      this.currentPosition = {
        lat: this.round(this.shelterLatControl.value, 8),
        lng: this.currentPosition.lng
      }
      this.map.panTo(this.currentPosition);
    }
  }

  //Googleマップと経度のフォーム連携
  onBlurMarkerLng() {
    if (!!this.shelterLngControl.valid) {
      this.shelterLngControl.setValue(this.round(this.shelterLngControl.value, 8));
      this.currentPosition = {
        lat: this.currentPosition.lat,
        lng: this.round(this.shelterLngControl.value, 8)
      }
      this.map.panTo(this.currentPosition);
    }
  }

  //以下、フォームの記入エラー表示
  // フォームの記入エラー表示部分
  public getErrorMessageToShelterId() {
    return this.shelterIdControl.hasError('required')
      ? this.translate.instant('必須項目')
    : this.shelterIdControl.hasError('pattern')
          ? this.translate.instant('数値以外')
          : '';
  }

  public getErrorMessageToShelterName() {
    return this.shelterNameControl.hasError('required')
      ? this.translate.instant('必須項目')
    : '';
  }

  public getErrorMessageToShelterLat() {
    return this.shelterLatControl.hasError('required')
      ? this.translate.instant('必須項目')
    : this.shelterLatControl.hasError('pattern') || this.shelterLatControl.hasError('min') || this.shelterLatControl.hasError('max')
          ? this.translate.instant('-90から90の間で小数第８位まで')
          : '';
  }

  public getErrorMessageToShelterLng() {
    return this.shelterLngControl.hasError('required')
      ? this.translate.instant('必須項目')
    : this.shelterLngControl.hasError('pattern') || this.shelterLngControl.hasError('min') || this.shelterLngControl.hasError('max')
          ? this.translate.instant('-180から180の間で小数第８位まで')
          : '';
  }

  //フォームの値を各要素に代入
  private createForm() {
    this.shelterFormGroup = this.formBuilder.group(
      {
        shelterId: [this.shelter!.shelter_id, [Validators.required, Validators.pattern('^[0-9]+$')]],
        shelterName: [this.shelter!.shelter_names, [Validators.required]],
        shelterLat: [this.shelter!.latitude, [Validators.required, Validators.min(-90), Validators.max(90), Validators.pattern('^(\-)?([1-9][0-9]{0,1}|0)(\.[0-9]{1,8})?$')]],
        shelterLng: [this.shelter!.longitude, [Validators.required, Validators.min(-180), Validators.max(180), Validators.pattern('^(\-)?([1-9][0-9]{0,2}|0)(\.[0-9]{1,8})?$')]],
        showMarker: [this.shelter!.show_marker, []],
      }
    );
  }

  //更新ボタン
  addShelter(): void {
    if (this.shelterFormGroup.valid) {
      this.shelter!.shelter_id = this.shelterIdControl.value;
      this.shelter!.shelter_names = this.shelterNameControl.value;
      this.shelter!.latitude = this.shelterLatControl.value;
      this.shelter!.longitude = this.shelterLngControl.value;
      this.shelter!.show_marker = this.showMarkerControl.value;
      if (this.shelter) {
        let request: ApiSetSheltersRequest = {
          access_token: '',
          change_type: this.data.change_type,
          shelters: [this.shelter]
        }
        this.spinnerService.openSpinner();
        this.apiClientService.setShelters(request).then(
          response => {
            if (response!.ret === AppConst.API_RET_SUCCESS) {
              this.toastr.success(this.translate.instant('登録しました。'));
              this.dialogRef.close();
            } else if (response!.ret === AppConst.API_RET_ALREADY_EXIST_DATA) {
              this.toastr.error(this.translate.instant('既に同じIDが登録済みです。'), '', { timeOut : 5000 });
            } else {
              console.error('api update error. ret='+response!.ret);
              this.toastr.error(this.translate.instant('登録できませんでした。'), '', { timeOut : 5000 });
            }
          }
        ).catch(
          error => {
            console.log(error);
            this.toastr.error(this.translate.instant('登録できませんでした。'), '', { timeOut : 5000 });
          }
        ).finally(
          () => {
            this.spinnerService.closeSpinner();
          }
        );
      }
    }
  }

  //属性名称ダイアログ
  openShelterNameDialog(): void {
    const dialogRef = this.dialog.open(MultiNameDialogComponent, {
      width: '60%',
      height: '90%',
      disableClose: true,
      data: { names: this.shelter!.shelter_names }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!!result) {
        this.shelter!.shelter_names = result;
        this.shelterNameControl.setValue(this.shelter!.shelter_names);
      }
    });
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  // マップ検索用のキー制御
  onKeyDownFromSearchPlace(keyCode: number): boolean {
    if (keyCode && keyCode === 13) {
      // NOTE: Enterを叩かれると他の項目の編集ボタンが反応してしまうのを防ぐ
      return false;
    }
    return true;
  }

  // マップ検索
  searchMap(): void {
    const places = this.searchBox.getPlaces();
    if (places!.length === 0) {
      return;
    }
    const bounds = new google.maps.LatLngBounds();
    places!.forEach(place => {
      if (!place.geometry || !place.geometry.location) {
        return;
      }
      if (place.geometry.viewport) {
        bounds.union(place.geometry.viewport);
      } else {
        bounds.extend(place.geometry.location);
      }
    });
    // console.log(bounds.getCenter().lat());
    // this.map.fitBounds(bounds);
    this.shelterLatControl.setValue(this.round(bounds.getCenter().lat(), 8));
    this.shelterLngControl.setValue(this.round(bounds.getCenter().lng(), 8));
    this.currentPosition = {
      lat: this.round(bounds.getCenter().lat(), 8),
      lng: this.round(bounds.getCenter().lng(), 8)
    }
    this.map.panTo(this.currentPosition);
  }

  // マップ検索フィールドクリア
  clearMapSearchField(): void {
    this.searchField.nativeElement.value = "";
  }

}
